<template>
  <div>
    <ApiLoading
      v-if="
        apiStatus.premiumImpactRefData.loading || apiStatus.premiumImpactRefData.fail || apiStatus.premiumAdjustmentTimeline.loading || apiStatus.premiumAdjustmentTimeline.fail
      "
      :apiError="apiStatus.premiumImpactRefData.fail || apiStatus.premiumAdjustmentPrices.fail"
    />
    <div v-else>
      <PremiumImpactInformation :showPremiumInformation="false" :refData="refData">
        <template #header>
          <h1>{{ refData.premiumImpactTitle.informationText }}</h1>
        </template>
        <template #headerInfoText>
          <div v-html="refData.premiumImpactSubtitle.informationText" />
        </template>
        <template #tableHeader>
          <h2>{{ refData.premiumImpactCardTitle.informationText }}</h2>
        </template>
        <template #productPremiumContent>
          <ProductSummaryCard>
            <template #left>
              <h3>{{ productName }}</h3>
              <p v-html="refData.premiumImpactCardParagraph.informationText" />
            </template>
            <template #right>
              <h4>Your premium</h4>
              <p class="text-green bold no-wrap">
                <span class="font-large">{{ convertToCurrencyString(premiumData.standardPremiumPaymentAmount) }}</span>
                /&nbsp;{{ premiumData.frequency }}
              </p>
              <p>
                Effective from
                <strong>{{ format(new Date(getCurrentDate()), 'dd MMMM yyyy') }}</strong>
              </p>
              <h4 class="text-green">Next payment* date {{ format(new Date(premiumData.futureContributionDate), 'dd MMMM yyyy') }}</h4>
              <p class="payment-caption">*Your next payment date may include a small catch up fee.</p>
              <button v-if="showPayNowCardButton" class="secondary" @click="handlePayNowBtnClick(this.premiumData.prorataPrice)">Pay Now</button>
            </template>
          </ProductSummaryCard>
        </template>
        <template #premiumAdjustmentTimeline>
          <ApiLoading
            class="api-loading"
            v-if="apiStatus.premiumAdjustmentTimeline.loading || apiStatus.premiumAdjustmentTimeline.fail"
            :apiError="apiStatus.premiumAdjustmentTimeline.fail"
          />
          <PremiumAdjustmentTimeline v-else :premiumTimelineData="premiumTimelineData" :refData="paymentTimelineRefData" @handlePayNowBtnClick="handlePayNowBtnClick" />
        </template>
        <template #relatedArticles>
          <h4>Related Articles</h4>
          <QuickLinks :linksData="relatedArticles" />
        </template>
      </PremiumImpactInformation>
    </div>
  </div>
</template>

<script>
import { PremiumImpactInformation, ApiLoading, ProductSummaryCard, QuickLinks, PremiumAdjustmentTimeline } from '@/components';
import PremiumImpactServices from '@/services/PremiumImpact';
import { MY_COVER_MAKE_A_PAYMENT } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import { convertToCurrencyString, getCurrentDate } from '@general/js/utils';
import { format } from 'date-fns';
import MemberDetailsMixin from './MemberDetailsFormSteps/MemberDetailsMixin';

export default {
  name: 'UpdateMemberDetailsPremiumImpact',

  components: {
    ApiLoading,
    PremiumImpactInformation,
    ProductSummaryCard,
    QuickLinks,
    PremiumAdjustmentTimeline,
  },

  mixins: [MemberDetailsMixin],

  async mounted() {
    this.getPremiumPrices();
  },

  data() {
    return {
      apiStatus: {
        premiumAdjustmentTimeline: {
          loading: false,
          fail: false,
        },
        premiumImpactRefData: {
          loading: false,
          fail: false,
        },
      },
      premiumTimelineData: [
        {
          paymentAmount: null,
          label: 'Difference to amount paid',
        },
        {
          paymentAmount: null,
          label: 'Next payment',
          date: null,
        },
        {
          paymentAmount: null,
          label: 'Payment onwards',
          date: null,
        },
      ],
      paymentTimelineRefData: {
        paymentAdjustmentInformationText: {
          title: 'Your payment has been adjusted',
          informationText: 'Any changes to your premium will be automatically adjusted in your next bill. Alternatively, you can pay for the remaining amount due now.',
        },
      },
      premiumData: null,
    };
  },

  computed: {
    showContents() {
      return this.refData?.premiumImpactInformationText?.informationText;
    },

    productName() {
      return this.membershipInfo?.productName;
    },

    relatedArticles() {
      return [this.refData?.premiumImpactRelatedArticle1, this.refData?.premiumImpactRelatedArticle2, this.refData?.premiumImpactRelatedArticle3];
    },

    showPayNowCardButton() {
      return this.premiumData?.prorataPrice > 0;
    },
  },

  methods: {
    async getPremiumPrices() {
      try {
        this.premiumAdjustmentPrices = await PremiumImpactServices.getPremiumAdjustmentPrices(this.$route?.params?.policyNumber, true, getCurrentDate());

        this.premiumTimelineData[0].paymentAmount = this.premiumAdjustmentPrices.prorataPrice;

        this.premiumTimelineData[1].paymentAmount = this.premiumAdjustmentPrices.standardPremiumPaymentAmount;
        this.premiumTimelineData[2].paymentAmount = this.premiumAdjustmentPrices.standardPremiumPaymentAmount;

        this.premiumTimelineData[1].date = this.premiumAdjustmentPrices.nextContributionDate;
        this.premiumTimelineData[2].date = this.premiumAdjustmentPrices.futureContributionDate;

        this.premiumData = this.premiumAdjustmentPrices;
      } catch (err) {
        logger.error(err);
        this.apiStatus.premiumAdjustmentTimeline.fail = true;
      } finally {
        this.apiStatus.premiumAdjustmentTimeline.loading = false;
      }
    },
    handlePayNowBtnClick(amt) {
      setNavigationState({ paymentAmount: amt });
      this.$router.push({ name: MY_COVER_MAKE_A_PAYMENT });
    },
    convertToCurrencyString,
    format,
  },
};
</script>

<style lang="scss" scoped>
.hidden {
  /** this class hides <h2> as per design but due to accessibility, <h2> is required, to pass accessibility, and screen reader, change opacity hence it is hidden and use as spacing between top content and quick links */
  opacity: 0.01; // use opacity instead of display none or visibility hidden to pass accessibility
  margin-bottom: 0;
  height: spacer(5); // for spacing between top content and quick links, which is 70px as per design suggested (20px is coming from <QuickLinks> style)
  overflow: hidden;
}

.payment-caption {
  font-size: 14px;
  margin-top: -1em;
}
</style>
